import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { SUPPLIER_CATEGORY_OPTIONS } from "@/api/suppliers.constraints";
import { useUpdateSupplierMutation } from "@/api/suppliers";
import { useGetActiveDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SupplierDetail } from "@/types/supplier";

interface Props {
  supplier: SupplierDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  name: string;
  external_id: string;
  owner_user_id: string;
  approved_supplier: boolean;
  preferred_supplier: boolean;
  critical_supplier: boolean;
}

export const EditSupplierDialog = ({ supplier, open, onOpenChange }: Props) => {
  const { data: owners = [] } = useGetActiveDirectoryUsersQuery();
  const [updateSupplier, { isLoading }] = useUpdateSupplierMutation();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { dirtyFields, errors },
  } = useForm<FormValues>();

  React.useEffect(() => {
    if (open)
      reset({
        name: supplier.name,
        external_id: supplier.external_id ?? "",
        owner_user_id: supplier.owner_user_id ?? "",
        approved_supplier: supplier.approved_supplier,
        preferred_supplier: supplier.preferred_supplier,
        critical_supplier: supplier.critical_supplier,
      });
  }, [
    open,
    reset,
    supplier.external_id,
    supplier.name,
    supplier.owner_user_id,
    supplier.approved_supplier,
    supplier.preferred_supplier,
    supplier.critical_supplier,
  ]);

  const submit = async (values: FormValues) => {
    try {
      const data = {
        ...(dirtyFields.name ? { name: values.name.trim() } : {}),
        ...(dirtyFields.external_id ? { external_id: values.external_id.trim() || null } : {}),
        ...(dirtyFields.owner_user_id ? { owner_user_id: values.owner_user_id || null } : {}),
        ...(dirtyFields.approved_supplier ? { approved_supplier: values.approved_supplier } : {}),
        ...(dirtyFields.preferred_supplier ? { preferred_supplier: values.preferred_supplier } : {}),
        ...(dirtyFields.critical_supplier ? { critical_supplier: values.critical_supplier } : {}),
      };
      await updateSupplier({
        supplierId: supplier.id,
        data,
      }).unwrap();
      toast.success("Supplier updated");
      onOpenChange(false);
    } catch (error) {
      const code = (error as { data?: { detail?: { code?: string } } })?.data?.detail?.code;
      if (code === "supplier_external_id_conflict")
        setError("external_id", { message: "This Supplier ID is already in use." });
      else if (code === "invalid_supplier_owner")
        setError("owner_user_id", { message: "Select an active user from this organization." });
      else setError("root.server", { message: "Supplier details could not be updated." });
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !isLoading && onOpenChange(next)}>
      <DialogContent closeDisabled={isLoading}>
        <form onSubmit={handleSubmit(submit)}>
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
          </DialogHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-supplier-name">Supplier name</Label>
              <Input
                id="edit-supplier-name"
                maxLength={255}
                {...register("name", { validate: value => Boolean(value.trim()) || "Supplier name is required" })}
              />
              {errors.name ? (
                <p role="alert" className="text-xs text-red-600">
                  {errors.name.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-supplier-id">Supplier ID</Label>
              <Input id="edit-supplier-id" maxLength={255} {...register("external_id")} />
              {errors.external_id ? (
                <p role="alert" className="text-xs text-red-600">
                  {errors.external_id.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-supplier-owner">Owner</Label>
              <select
                id="edit-supplier-owner"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                {...register("owner_user_id")}
              >
                <option value="">Unassigned</option>
                {owners
                  .filter(owner => owner.id !== supplier.owner_user_id)
                  .map(owner => (
                    <option key={owner.id} value={owner.id}>
                      {[owner.name, owner.surname].filter(Boolean).join(" ") || owner.email}
                    </option>
                  ))}
                {supplier.owner_user_id && !owners.some(owner => owner.id === supplier.owner_user_id) ? (
                  <option value={supplier.owner_user_id}>Current owner</option>
                ) : null}
              </select>
              {errors.owner_user_id ? (
                <p role="alert" className="text-xs text-red-600">
                  {errors.owner_user_id.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-3 border-t pt-4">
              <p className="text-sm font-semibold text-slate-900">Supplier categories</p>
              {SUPPLIER_CATEGORY_OPTIONS.map(([field, label]) => (
                <div key={field} className="flex items-center gap-2">
                  <input id={`edit-${field}`} type="checkbox" className="size-4 accent-primary" {...register(field)} />
                  <Label htmlFor={`edit-${field}`}>{label}</Label>
                </div>
              ))}
            </div>
          </div>
          {errors.root?.server ? (
            <p role="alert" className="mt-4 text-sm text-red-700">
              {errors.root.server.message}
            </p>
          ) : null}
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
